<i18n>
{
  "en": {
    "title": "Transfer",
    "transfer": {
      "betweenWallets": "Transfer Between Wallets",
      "inputs": {
        "from": "From",
        "transfer": "Transfer to",
        "amount": "Amount"
      },
      "fundingWallet": "Funding Wallet",
      "spotWallet": "Spot Wallet",
      "transferHistory": "Transfer History",
      "tableColumns": {
        "from": "From",
        "to": "To",
        "amount": "Amount",
        "date": "Date"
      },
      "tablePlaceholder": "Search by currency..."
    },
    "userTransfer": {
      "userTransfer": "Transfer To User",
      "inputs": {
        "recipient": "Recipient ID or {0} Wallet Address",
        "recipient2": "Recipient",
        "amount": "Amount"
      },
      "userTransferHistory": "User Transfer History",
      "tableColumns": {
        "sender": "Sender",
        "recipient": "Recipient",
        "amount": "Amount",
        "date": "Date"
      },
      "tablePlaceholder": "Search by sender, recipient, currency...",
      "me": "Me"
    },
    "fee": "Without commission",
    "completed": "Completed",
    "error": "Transfer error"
  },
  "zh": {
    "title": "转移",
    "transfer": {
      "betweenWallets": "钱包间转账",
      "inputs": {
        "from": "从",
        "transfer": "转移至",
        "amount": "数量"
      },
      "fundingWallet": "资金钱包",
      "spotWallet": "现货钱包",
      "transferHistory": "转会记录",
      "tableColumns": {
        "from": "从",
        "to": "发给",
        "amount": "数量",
        "date": "日期"
      },
      "tablePlaceholder": "按货币搜索..."
    },
    "userTransfer": {
      "userTransfer": "转移给用户",
      "inputs": {
        "recipient": "接受者",
        "recipient2": "接受者",
        "amount": "数量"
      },
      "userTransferHistory": "用户转移历史",
      "tableColumns": {
        "sender": "发件人",
        "recipient": "接受者",
        "amount": "数量",
        "date": "日期"
      },
      "tablePlaceholder": "按发件人、收件人、货币搜索...",
      "me": "我"
    },
    "fee": "无佣金",
    "completed": "完毕",
    "error": "错误"
  },
  "kr": {
    "title": "전송",
    "transfer": {
      "betweenWallets": "지갑간 이체",
      "inputs": {
        "from": "에서",
        "transfer": "전송",
        "amount": "금액"
      },
      "fundingWallet": "펀딩 지갑",
      "spotWallet": "현물 지갑",
      "transferHistory": "전송 기록",
      "tableColumns": {
        "from": "에서",
        "to": "~에",
        "amount": "금액",
        "date": "날짜"
      },
      "tablePlaceholder": "통화로 검색..."
    },
    "userTransfer": {
      "userTransfer": "사용자에게 전송",
      "inputs": {
        "recipient": "받는 사람",
        "recipient2": "받는 사람",
        "amount": "금액"
      },
      "userTransferHistory": "사용자 전송 기록",
      "tableColumns": {
        "sender": "발신자",
        "recipient": "받는 사람",
        "amount": "금액",
        "date": "날짜"
      },
      "tablePlaceholder": "보낸 사람, 받는 사람, 통화로 검색...",
      "me": "나"
    },
    "fee": "수수료 없음",
    "completed": "완전한",
    "error": "오류"
  }
}
</i18n>

<template>
  <Page :is-page-loaded="dataLoadingStatus" :title="$t('title')">
    <PageContainer :no-line="true">
      <div class="container-fluid row" style="margin-bottom: 40px;">
        <div class="col-lg-6 col-md-12 col-xs-12">
          <h2 style="font-size: 18px;">{{ $t('transfer.betweenWallets') }}</h2>
          <div style="margin-bottom: 15px;">
            <CoinsDropInput
                :on-item-change="changeCurrentTransferCoin"
                :coins-object="coinList"
                :balance="balance"
                :type="selectControls.fromSelect.value + 'Wallet'"
                style="margin-bottom: 30px;"
            />
            <div class="section">
              <div class="row" style="margin: 0; position: relative;">
                <SelectBox
                    v-for="control, index in selectControls"
                    class="col-lg-6 col-md-6 col-xs-6"
                    :style="{ padding: index === 'fromSelect' ? '0 15px 0 0 !important' : '0 0 0 15px !important' }"
                    :key="index"
                    :options="control.options"
                    :label="$t(control.label)"
                    :validation="control.validation"
                    v-model="control.value"
                    @check-valid="control.isValid = $event"
                    @input="changeWalletTransferInputs(index)"
                />
              </div>
              <InputBox
                  type="number"
                  :label="$t(formControls.transfers.amountInput.label)"
                  v-model="formControls.transfers.amountInput.value"
                  :validation="formControls.transfers.amountInput.validation"
                  @check-valid="formControls.transfers.amountInput.isValid = $event"
              />
              <Button
                  custom-width="100%"
                  :is-async="true"
                  :callback="transferHandler"
                  :is-disabled="!$isFormValid(Object.assign(formControls.transfers, selectControls))"
              >
                {{ $t('title') }}
              </Button>
              <div style="margin-top: 20px; font-weight: 500; letter-spacing: .01em;">
                {{ $t('fee') }}
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-6 col-md-12 col-xs-12">
          <h2 style="font-size: 18px;">{{ $t('userTransfer.userTransfer') }}</h2>
          <div style="margin-bottom: 15px;">
            <CoinsDropInput
                :on-item-change="changeCurrentUserTransferCoin"
                :coins-object="coinList"
                :balance="balance"
                style="margin-bottom: 30px;"
            />
            <div class="section">
              <div style="position: relative;">
                <InputBox
                    v-for="control, index of formControls.userTransfers"
                    :key="index"
                    :type="control.type"
                    :label="$t(control.label, [$store.state.Index.websiteName.short])"
                    v-model="control.value"
                    :validation="control.validation"
                    @check-valid="control.isValid = $event"
                />
                <span style="position: absolute; font-size: 11px; top: 120px; right: 10px; color: #818481;" v-show="formControls.userTransfers.amountInput.value">
                ~{{ formControls.userTransfers.amountInput.value * coinList.find(x => x.code === currentUserTransferCoin).usdPrice | usdFormat }}
                </span>
              </div>
              <Button
                  custom-width="100%"
                  :is-async="true"
                  :callback="userTransferHandler"
                  :is-disabled="!$isFormValid(formControls.userTransfers)"
                  :answer-delay="1000"
              >
                {{ $t('title') }}
              </Button>
              <div style="margin-top: 20px; font-weight: 500; letter-spacing: .01em;">
                {{ $t('fee') }}
              </div>
            </div>
          </div>
        </div>

      </div>

      <div :style="{ 'border-top-color': $store.state.Index.isDarkMode ? '#363b3d' : '#e8eaf0'}" style="border-top: 1px solid transparent; padding-top: 40px;" class="container-fluid row">

        <div style="float: right" class="col-lg-6 col-md-12 col-xs-12">
          <h2 style="font-size: 18px;">{{ $t('userTransfer.userTransferHistory') }}</h2>
          <OnlineTable
              parse-url="/main/user/userTransfer"
              :column-list="tableUserTransfer.columns"
              default-sort="date"
              :search-placeholder-text="$t('userTransfer.tablePlaceholder')"
              @handle-data="tableUserTransfer.data = $event"
          >
            <tr v-for="data, index in tableUserTransfer.data" :key="index">
              <td>{{ data.userInfo.email === $store.state.Index.userInfo.email ? $t('userTransfer.me') : hideUserEmail(data.userInfo.email) }}</td>
              <td>{{ data.recipientInfo.email === $store.state.Index.userInfo.email ? $t('userTransfer.me') : hideUserEmail(data.recipientInfo.email) }}</td>
              <td>
                <div style="display: flex; align-items: center; width: 100px;">
                  <div style="width: 16px; height: 16px; margin-right: 5px; min-width: 16px;">
                    <img style="height: 100%; vertical-align: unset;" :src="$coinImage(data.currency)" alt="">
                  </div>
                  {{ data.amount.toFixed(8) }}
                </div>
              </td>
              <td style="min-width: 200px;">{{ data.date | toDate }}</td>
            </tr>
          </OnlineTable>
        </div>


        <div style="float: right" class="col-lg-6 col-md-12 col-xs-12">
          <h2 style="font-size: 18px;">{{ $t('transfer.transferHistory') }}</h2>
          <OnlineTable
              parse-url="/main/user/getTransfers"
              :column-list="tableTransfer.columns"
              default-sort="date"
              :search-placeholder-text="$t('transfer.tablePlaceholder')"
              @handle-data="tableTransfer.data = $event"
          >
            <tr v-for="data, index in tableTransfer.data" :key="index">
              <td>{{  $t('transfer.' + data.fromWallet) }}</td>
              <td>{{  $t('transfer.' + data.toWallet) }}</td>
              <td>
                <div style="display: flex; align-items: center; width: 100px;">
                  <div style="width: 16px; height: 16px; margin-right: 5px; min-width: 16px;">
                    <img style="height: 100%; vertical-align: unset;" :src="$coinImage(data.currency)" alt="">
                  </div>
                  {{ data.amount.toFixed(8) }}
                </div>
              </td>
              <td>{{ data.date | toDate }}</td>
            </tr>
          </OnlineTable>
        </div>
      </div>

      <modal
          name="withdrawal-complete"
          height="auto"
          width="384px"
          :clickToClose="false"
          :adaptive="true"
      >
        <div class="trade-complete">
          <div class="css-81xrsn">
            <svg v-if="transferStatus.isError" style="box-sizing: border-box; margin: 0px; min-width: 0px; color: rgb(246, 70, 93); font-size: 90px; fill: rgb(246, 70, 93); width: 1em;height: 1em" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" class="css-1xb7vb9"><path fill-rule="evenodd" clip-rule="evenodd" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-7.233 0l3.006 3.005-1.768 1.768L12 13.767l-3.005 3.005-1.768-1.768 3.005-3.005-3.005-3.005 1.768-1.767L12 10.23l3.005-3.005 1.768 1.767L13.767 12z" fill="currentColor"></path></svg>
            <svg v-else style="box-sizing: border-box; margin: 0px; min-width: 0px; color: currentcolor; font-size: 90px; fill: currentcolor; width: 1em;height: 1em" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" fill="none" class="css-ade6fb"><g clip-path="url(#success-b96_svg__clip0)" fill="#00C087"><path d="M71 25a32.523 32.523 0 00-22.96-9.52c-17.95-.01-32.51 14.53-32.52 32.48C15.51 65.91 30.05 80.47 48 80.48c17.95.01 32.51-14.53 32.52-32.48 0-8.63-3.42-16.9-9.52-23zM42.04 60.5L30.97 49.43l2.12-2.12 8.95 8.95 20.91-20.91 2.13 2.13L42.04 60.5zM46.48 2.51L46.56 5H49l.03-2.49h-2.55zm5.65.16l-.22 2.49c.79.07 1.6.17 2.39.28l.36-2.47c-.84-.12-1.69-.22-2.53-.3zm-8.7 0c-.84.08-1.69.19-2.52.33l.39 2.51c.78-.13 1.59-.23 2.38-.35l-.25-2.49zm14.19.83l-.53 2.44c.78.17 1.57.37 2.34.58l.66-2.41c-.81-.23-1.64-.43-2.47-.61zm-19.71.11c-.83.19-1.66.4-2.47.64l.69 2.4c.77-.22 1.56-.42 2.33-.6l-.55-2.44zM63 5l-.79 2.38c.75.26 1.51.55 2.25.86l.92-2.33C64.6 5.58 63.8 5.28 63 5zm-30.46.19c-.79.29-1.59.61-2.37.94l1 2.29c.73-.32 1.48-.61 2.22-.88l-.85-2.35zm-1.37 3.24v-.01c-.01 0-.01.01-.02.01h.02zm36.99-1.24l-1.11 2.24c.71.36 1.43.74 2.12 1.13l1.24-2.17c-.74-.42-1.5-.82-2.25-1.2zm-40.78.24c-.75.38-1.5.79-2.23 1.22l1.26 2.16h.02c.69-.41 1.4-.81 2.09-1.16l-1.14-2.22zM73 10l-1.39 2.07c.69.44 1.36.9 2 1.38l1.48-1.99C74.41 11 73.71 10.47 73 10zm-50.44.26c-.7.49-1.39.97-2.07 1.49l1.51 2c.66-.49 1.32-.96 2-1.41l-1.44-2.08zm54.94 3.08l-1.62 1.91c.61.52 1.21 1.06 1.79 1.61l1.72-1.81c-.59-.59-1.22-1.16-1.89-1.71zm-59.37.34c-.63.56-1.26 1.14-1.87 1.73L18 17.2c.58-.56 1.18-1.11 1.77-1.63l-1.64-1.89zm63.42 3.55l-1.85 1.69c.58.59 1.07 1.2 1.58 1.82l1.93-1.58c-.53-.65-1.09-1.3-1.66-1.93zm-67.4.38c-.56.63-1.12 1.28-1.65 1.94l1.95 1.57c.55-.63 1.03-1.24 1.55-1.84l-1.85-1.67zm70.92 3.97l-2 1.45c.46.65.92 1.33 1.34 2l2.08-1.33c-.42-.72-.93-1.43-1.42-2.12zm-74.4.42c-.48.7-.95 1.41-1.39 2.13l2.13 1.31c.41-.68.86-1.35 1.32-2.01L10.67 22zM88 26.33l-2.22 1.19c.38.7.75 1.43 1.09 2.15l2.28-1.07c-.36-.76-.75-1.53-1.15-2.27zm-80.28.5c-.39.74-.77 1.51-1.12 2.28l2.27 1h.04c.33-.73.69-1.45 1.09-2.11l-2.28-1.17zm82.66 4.58l-2.33.91c.29.74.57 1.51.82 2.27l2.37-.79c-.26-.8-.55-1.61-.86-2.39zm-84.92.48c-.3.8-.58 1.61-.83 2.41l2.37.75c.25-.76.51-1.52.8-2.27l-2.34-.89zm86.65 4.85l-2.42.62c.19.77.37 1.56.53 2.34l2.45-.48c-.16-.83-.35-1.66-.56-2.48zm-88.3.51c-.2.82-.38 1.66-.53 2.49l2.46.45c.14-.78.31-1.58.5-2.35l-2.43-.59zm89.34 4.99l-2.48.31c.1.79.18 1.6.24 2.4l2.49-.18c-.06-.84-.14-1.69-.25-2.53zm-90.32.52c-.1.84-.22 1.7-.22 2.54l2.5.15c.04-.8.11-1.61.2-2.4l-2.48-.29zm88.23 5.07v.14c0 .8-.06 1.61-.06 2.41l2.44.14c.05-.84.07-1.7.12-2.52v-.17h-2.5zm-86.03.53h-2.5c.01.84.04 1.7.1 2.54l2.49-.14c-.09-.79-.09-1.6-.09-2.4zm85.66 4.9c-.1.8-.22 1.6-.36 2.38l2.46.44c.15-.83.28-1.67.38-2.52l-2.48-.3zm-85.29.36l-2.48.32c.11.84.25 1.69.4 2.52l2.46-.47a37.7 37.7 0 01-.38-2.37zm84.32 4.84c-.19.78-.41 1.56-.65 2.32l2.39.74c.25-.8.48-1.63.69-2.45l-2.43-.61zm-83.31.35l-2.42.63c.21.82.45 1.64.71 2.44l2.38-.76c-.24-.76-.47-1.54-.67-2.31zm81.71 4.73c-.28.74-.6 1.49-.93 2.22l2.28 1c.35-.76.68-1.56.98-2.32l-2.33-.9zm-80.07.3l-2.32.92c.31.78.65 1.57 1 2.34L9 66.04c-.33-.72-.65-1.46-.95-2.2zm80.07 5.64l-2.21-1.18c-.37.7-.78 1.41-1.19 2.09l2.13 1.3v.02c.44-.71.87-1.47 1.27-2.23zM10.3 68.6l-2.19 1.2c.4.74.84 1.48 1.28 2.2l2.13-1.32c-.43-.68-.84-1.38-1.22-2.08zm72.78 4.22c-.44.65-.95 1.3-1.44 1.93l2 1.55h.01c.52-.66 1.04-1.3 1.43-2.04l-2-1.44zm-69.99.3l-2 1.46c.5.66 1.01 1.34 1.55 2l1.91-1.54c-.46-.62-.99-1.27-1.46-1.92zm66.73 3.83c-.46.47-.92 1-1.4 1.47l-.27.27 1.76 1.78.28-.28c.48-.51 1-1.02 1.48-1.56l-1.85-1.68zm-63.35.24l-1.84 1.7c.57.62 1.17 1.23 1.78 1.82l1.74-1.8c-.58-.55-1.14-1.13-1.68-1.72zm59.54 3.43c-.6.52-1.23 1.03-1.87 1.52l1.52 2h.01c.67-.51 1.33-1.05 1.97-1.62l-1.63-1.9zm-55.74.23l-1.61 1.91c.66.59 1.33 1.08 2 1.59l1.49-1.99c-.64-.51-1.27-.99-1.88-1.51zm51.46 2.98c-.62.45-1.31.88-2 1.28L71 87.27c.76-.43 1.48-.89 2.12-1.36l-1.39-2.08zm-47.2.17l-1.4 2.11c.71.47 1.44.92 2.17 1.34l1.23-2.19c-.64-.4-1.33-.82-2-1.26zm42.65 2.51c-.71.34-1.45.68-2.18 1l1 2.29c.81-.34 1.59-.7 2.3-1.06l-1.12-2.23zm-38.05.18l-1.1 2.25c.76.37 1.54.73 2.32 1.06l.97-2.31c-.73-.3-1.47-.64-2.19-1zm33.23 1.87c-.76.27-1.53.52-2.29.74l.7 2.4c.81-.24 1.62-.5 2.42-.78l-.83-2.36zM34 88.68l-.79 2.36c.8.28 1.62.54 2.43.77l.66-2.41c-.77-.22-1.54-.46-2.3-.72zm23.27 1.34c-.78.17-1.57.32-2.36.45l.4 2.47c.83-.14 1.69-.3 2.5-.48l-.54-2.44zm-18.15.07l-.52 2.45c.83.17 1.68.33 2.51.46l.38-2.48c-.79-.12-1.59-.26-2.37-.43zm12.93.75c-.8.08-1.6.16-2.4.16l.09 2.49c.84-.03 1.7-.09 2.54-.16l-.23-2.49zm-7.7.02l-.21 2.5c.85.07 1.7.14 2.54.14l.08-2.5c-.8-.02-1.61-.07-2.41-.14z"></path></g><defs><clipPath id="success-b96_svg__clip0"><path fill="#fff" d="M0 0h96v96H0z"></path></clipPath></defs></svg></div>
          <div style="margin-top: 18px; font-weight: 500; color: rgb(30, 35, 41); font-size: 20px; line-height: 24px;">
            {{ $t(transferStatus.isError ? 'error' : 'completed') }}
          </div>
          <div v-show="!transferStatus.isError">
            <div style="margin-top: 32px; color: rgb(72, 81, 93); font-size: 14px; line-height: 18px;">{{ $t('userTransfer.inputs.amount') }}</div>
            <div style="line-height: 34px; font-size: 24px; color: rgb(30, 35, 41); margin-top: 6px; margin-bottom: 30px;">{{ (formControls.userTransfers.amountInput.value - 0).toFixed(6) }} {{ currentUserTransferCoin }}</div>
          </div>

          <div v-if="transferStatus.isError">
            <div style="margin-top: 32px; color: rgb(72, 81, 93); font-size: 14px; line-height: 18px;">{{ transferStatus.message }}</div>
          </div>
          <div v-else>
            <div class="quote-infos">
              <div>{{ $t('userTransfer.inputs.recipient2') }}</div>
              <div class="value">{{ hideUserEmail(formControls.userTransfers.recipientInput.value) }}</div>
            </div>
          </div>

          <div style="display: flex; justify-content: space-between; margin-top: 20px;">
            <Button
                custom-width="100%"
                style="background: rgb(234, 236, 239); color: rgb(30, 35, 41);"
                :is-async="false"
                :callback="() => $router.go()"
            >
              {{ $t('close') }}
            </Button>
          </div>
        </div>
      </modal>

    </PageContainer>
  </Page>
</template>

<script>
import PageContainer from "@/components/main/UI/PageContainer";
import Page from "@/components/main/UI/Page";
import CoinsDropInput from "@/components/main/UI/CoinsDropInput";
import axios from "@/plugins/axios";
import OnlineTable from "@/components/main/UI/OnlineTable";
import InputBox from "@/components/main/UI/InputBox";
import SelectBox from "@/components/main/UI/SelectBox";
import Button from "@/components/main/UI/Button";
import TwoFactorModal from "@/mixins/two-factor-modal";

export default {
  name: "Transfer",
  components: {
    PageContainer,
    Page,
    OnlineTable,
    CoinsDropInput,
    InputBox,
    Button,
    SelectBox
  },
  mixins: [TwoFactorModal],
  data() {
    return {
      transferStatus: {
        isError: false,
        message: ""
      },
      selectControls: {
        fromSelect: {
          value: 'funding',
          label: 'transfer.inputs.from',
          isValid: true,
          validation: {
            required: true
          },
          options: [
            {
              title: this.$t('transfer.fundingWallet'),
              value: 'funding'
            },
            {
              title: this.$t('transfer.spotWallet'),
              value: 'spot'
            },
          ]
        },
        toSelect: {
          value: 'spot',
          label: 'transfer.inputs.transfer',
          isValid: true,
          validation: {
            required: true
          },
          options: [
            {
              title: this.$t('transfer.fundingWallet'),
              value: 'funding'
            },
            {
              title: this.$t('transfer.spotWallet'),
              value: 'spot'
            },
          ]
        },
      },
      formControls: {
        transfers: {
          amountInput: {
            value: '',
            type: 'number',
            label: 'transfer.inputs.amount',
            isValid: false,
            validation: {
              required: true
            }
          }
        },
        userTransfers: {
          recipientInput: {
            value: '',
            type: 'text',
            label: 'userTransfer.inputs.recipient',
            isValid: false,
            validation: {
              required: true
            }
          },
          amountInput: {
            value: '',
            type: 'number',
            label: 'userTransfer.inputs.amount',
            isValid: false,
            validation: {
              required: true
            }
          }
        }
      },
      tableTransfer: {
        data: [],
        columns: [
          {
            name: 'fromWallet',
            title: this.$t('transfer.tableColumns.from'),
            isSortable: true
          },
          {
            name: 'toWallet',
            title: this.$t('transfer.tableColumns.to'),
            isSortable: true
          },
          {
            name: 'amount',
            title: this.$t('transfer.tableColumns.amount'),
            isSortable: true
          },
          {
            name: 'date',
            title: this.$t('transfer.tableColumns.date'),
            isSortable: true
          }
        ]
      },
      tableUserTransfer: {
        data: [],
        columns: [
          {
            name: 'userInfo.email',
            title: this.$t('userTransfer.tableColumns.sender'),
            isSortable: true
          },
          {
            name: 'recipientInfo.email',
            title: this.$t('userTransfer.tableColumns.recipient'),
            isSortable: true
          },
          {
            name: 'amount',
            title: this.$t('userTransfer.tableColumns.amount'),
            isSortable: true
          },
          {
            name: 'date',
            title: this.$t('userTransfer.tableColumns.date'),
            isSortable: true
          }
        ]
      },
      dataLoadingStatus: {
        isDone: false,
        isError: false
      },
      currentTransferCoin: this.$route.params.coin || 'BTC',
      currentUserTransferCoin: this.$route.params.coin || 'BTC',
      coinList: {},
      balance: {},
      userLimit: 0
    }
  },
  methods: {
    openModal() {
      this.$modal.show('withdrawal-complete');
    },
    changeWalletTransferInputs(select) {
      if (this.selectControls[select].value === 'funding') {
        this.selectControls[select === 'fromSelect' ? 'toSelect' : 'fromSelect'].value = 'spot';
      } else {
        this.selectControls[select === 'fromSelect' ? 'toSelect' : 'fromSelect'].value = 'funding';
      }
    },
    changeCurrentTransferCoin(coin) {
      this.currentTransferCoin = coin;
    },
    changeCurrentUserTransferCoin(coin) {
      this.currentUserTransferCoin = coin;
    },
    async transferHandler() {
      try {
        const fetchedData = await axios.post('/main/user/transfer', {
          from: this.selectControls.fromSelect.value,
          to: this.selectControls.toSelect.value,
          amount: this.formControls.transfers.amountInput.value,
          coin: this.currentTransferCoin
        });
        this.$callNotification(
            this.$i18n.locale,
            fetchedData
        );
        if (!fetchedData.data.error) return location.reload();
      } catch (error) {
        console.log(error)
        this.$callNotification(this.$i18n.locale, error);
      }
    },
    async userTransferHandler() {
      try {
        const fetchedData = await axios.post('/main/user/userTransfer', {
          recipient: this.formControls.userTransfers.recipientInput.value,
          amount: this.formControls.userTransfers.amountInput.value,
          coin: this.currentUserTransferCoin
        });
        if (fetchedData.data.withdrawError) {
          this.transferStatus.isError = true;
          this.transferStatus.message = fetchedData.data.description;
          this.openModal();
        } else if (fetchedData.data.error) {
          this.$callNotification(
              this.$i18n.locale,
              {
                error: fetchedData.data.error,
                message: fetchedData.data.description,
                timeout: fetchedData.data.withdrawError ? -1 : null,
                code: fetchedData.status
              }
          );
        } else {
          this.hideTwoFactorConfirmation();
          if (fetchedData.status === 206) return this.openTwoFactorConfirmation(this.withdrawHandler);
          this.transferStatus.isError = false;
          this.openModal();
        }
      } catch (error) {
        console.log(error)
        this.$callNotification(this.$i18n.locale, error);
      }
    },
    hideUserEmail(email) {
      return email.slice(0, 3) + "***" + email.slice(email.indexOf("@"), email.length);
    }
  },
  async mounted() {
    try {
      let fetchedData = await axios.get('main/user/transfer');
      if (fetchedData.data.error) {
        this.dataLoadingStatus.isDone = true;
        this.dataLoadingStatus.isError = true;
        return;
      }
      this.coinList = fetchedData.data.coinList;
      this.balance = fetchedData.data.balance;
      this.userLimit = fetchedData.data.userLimit;
      this.dataLoadingStatus.isDone = true;
    } catch (e) {
      console.log(e)
      this.dataLoadingStatus.isDone = true;
      this.dataLoadingStatus.isError = true;
    }
  }
}
</script>

<style lang="scss" scoped>

.dark {
  .trade-complete {
    box-shadow: rgba(24, 25, 30, 0.1) 0px 0px 1px, rgba(60, 64, 67, 0.04) 0px 3px 6px, rgba(24, 25, 30, 0.04) 0px 1px 2px;
    background-color: rgb(28, 30, 31);
    color: #c4c0bb;

    div { color: #c4c0bb !important; }

  }
}

@media (max-width: 385px) {
  .address {
    text-align: left !important;
  }
}

.address-info {
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -webkit-flex-direction: column;
  -ms-flex-direction: column;
  flex-direction: column;
  border: 1px solid #edf0f5;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 8px;

  .qr-code {
    text-align: center;

    img {
      width: 100%;
      height: 100%;
      max-width: 250px;
    }
  }

  .address {
    background-color: transparent;
    color: #0a0f38;
    padding: 16px;
    position: relative;
    height: 52px;
    border-top: 1px solid #edf0f5;
    font-size: 13px;
    text-align: center;

    .copy {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      padding: 12px;
      background-color: transparent;
      border: none;
      cursor: pointer;
      -webkit-transition: .25s ease-out;
      -o-transition: .25s ease-out;
      transition: .25s ease-out;
      align-items: center;
      display: flex;
    }
  }
}

td.hash {
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 200px;
  white-space: nowrap;
}

.change-wallet {
  font-size: 20px;
  position: absolute;
  transform: translate(-50%, -50%);
  top: calc(50% + 12px);
  left: 50%;
  transition: .3s;
  cursor: pointer;

  &:hover {
   color: grey;
  }
}
.trade-complete {
  width: 384px;
  padding: 26px 36px 40px;
  box-sizing: border-box;
  min-width: 0px;
  margin: auto;
  display: flex;
  border-radius: 4px;
  box-shadow: rgba(24, 26, 32, 0.1) 0px 0px 1px, rgba(71, 77, 87, 0.04) 0px 3px 6px, rgba(24, 26, 32, 0.04) 0px 1px 2px;
  background-color: rgb(255, 255, 255);
  text-align: center;
  -webkit-box-align: stretch;
  align-items: stretch;
  flex-direction: column;
}

.quote-infos {
  margin-bottom: 10px;
  align-items: center;
  display: flex;
  justify-content: space-between;

  .value {
    font-size: 12px;
  }
}

</style>